import { useEffect, useState, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from "react-router-dom"
import classes from './CyberAssessment.module.scss'
import PageHeading from '../../../shared/PageHeading'
import useAssessment from '../../../../customHooks/useAssessment'
import ProgressBar from '../ProgressBar'
import Button from '../../../shared/ComponentLibrary/RButton'
import Icon from '../../../../Icon'
import Timer from '../Timer'
import PulseWheel from '../PulseWheel'
import { toast } from 'react-toastify'
import CyberAssessmentFeedback from './CyberAssessmentFeedback'
import { scrollDocToTop } from '../../../../helpers/methods'
import { datadogLogs } from '@datadog/browser-logs'
import { useSelector } from 'react-redux'

const StartCyberAssessment = () => {
  const navigate = useNavigate()
  const { startCyberAssessment, submitCyberAssessment, cancelCyberAssessment } = useAssessment()
  const [error, setError] = useState('')
  const [status, setStatus] = useState<IStatus>({ status: false })
  const [cyberAssessmentData, setCyberAssessmentData] = useState<ICyberAssessmentData>({})
  const [documentNumber, setDocumentNumber] = useState(1)
  const [eligible, setEligible] = useState(false)
  const [resetTimer, setResetTimer] = useState(false)
  const [pulseWheelTimeUp, setPulseWheelTimeUp] = useState(false)
  const { register, reset, handleSubmit } = useForm()
  const scrollRef = useRef<any>()
  const profileStatus: UserProfileStatus = useSelector((state: ProfileState) => state.profile)

  useEffect(() => {
    if (profileStatus.profileStatus) {
      startCyberAssessment({ setCyberAssessmentData, setError })
    }
  }, [profileStatus.profileStatus])
  useEffect(() => console.log(error), [error])

  useEffect(() => {
    status.status && navigate('/')
    toast.info(status.message)
  }, [status])

  useEffect(() => {
    if (!profileStatus.profileStatus) {
      navigate('/')
      toast.error('Profile Incomplete!')
    }
  }, [])

  useEffect(() => {
    if (cyberAssessmentData.eligible?.status === false) {
      navigate('/')
      toast.info(cyberAssessmentData.eligible?.message)
    }
    else {
      setEligible(true)
      cyberAssessmentData.cyberTestInstance?.documentNumber && setDocumentNumber(cyberAssessmentData.cyberTestInstance.documentNumber)
    }
  }, [cyberAssessmentData?.eligible])

  useEffect(() => console.log(cyberAssessmentData), [cyberAssessmentData])

  const submitHandler = (data: any) => {
    let successHandler = (submitData: ISubmitCyberResponseData) => {
      setDocumentNumber((current) => current + 1)
      reset()
      datadogLogs.logger.info('Assessment: Document Questions Submitted', { documentNumberInAssessment: documentNumber, details: submitData });
      scrollDocToTop(scrollRef)
      setResetTimer((current) => !current)
    }
    let errorHandler = () => {
      console.log("assessment failed to submit")
    }

    if (cyberAssessmentData.cyberDocument) {
      let submitData: ISubmitCyberResponseData = {
        attributes: {
          cyberDocumentId: cyberAssessmentData.cyberDocument.id,
          cyberResponse: []
        },
        setCyberAssessmentData: setCyberAssessmentData,
        setError: setError
      }
      Object.keys(data).forEach((key) => {
        if (data[key]) {
          submitData.attributes?.cyberResponse.push({ cyberQuestionId: parseInt(key), answer: data[key] })
        }
      })

      // Check if all questions are answered
      const questionIds = cyberAssessmentData.cyberTestInstance?.cyberQuestions.map(q => q.id) || [];
      const answeredIds = submitData.attributes!.cyberResponse.map(r => r.cyberQuestionId.toString());
      const allAnswered = questionIds.every(id => answeredIds.includes(id.toString()));

      if (!allAnswered) {
        toast.warning('Please answer all questions before submitting.');
        return;
      }
      console.log(submitData)

      submitCyberAssessment(submitData, successHandler, errorHandler)
    }
  }

  const readyToShowPages = () => Boolean(cyberAssessmentData.cyberDocument?.content.length)
  const forceDoNotShowPages = () => Boolean(!readyToShowPages() || pulseWheelTimeUp)

  return (
    <div className={classes.assessment_container}>
      {
        cyberAssessmentData?.isCompleted ?
          <CyberAssessmentFeedback /> :
          eligible &&
          <div>
            <div className={classes.header}>
              <PageHeading title='Cyber Skills Assessment' icon='testResults' />
              {cyberAssessmentData.cyberTestInstance && <Timer seconds={cyberAssessmentData.cyberTestInstance?.secondsRemaining} showIcon design='total' />}
            </div>

            <ProgressBar currentValue={documentNumber - 1} maxValue={15} />

            <form onSubmit={handleSubmit(submitHandler)}>
              <div className={`${classes.assessment_section} ${forceDoNotShowPages() ? classes.pulse_wheel_holder : ''}`}>
                <PulseWheel show={!readyToShowPages()} style={{ borderColor: "#2CB6D1", borderTopColor: "transparent" }} timer={60} timeUp={pulseWheelTimeUp} setTimeUp={setPulseWheelTimeUp}>
                  <div>
                    <p>The connection appears to be unstable. Please try again later.</p>
                    <p>We recommend taking the assessment from a desktop computer with a stable connection.</p>
                  </div>
                </PulseWheel>
                <div className={`${classes.title_section} ${forceDoNotShowPages() ? classes.hidden : ''}`}>
                  <div className={classes.title}>
                    <b>Document {documentNumber}:</b> "{cyberAssessmentData.cyberDocument && cyberAssessmentData.cyberDocument.title}"
                  </div>
                  {cyberAssessmentData.cyberTestInstance && <Timer seconds={cyberAssessmentData.cyberTestInstance?.secondsElapsed} incremental resetTimer={resetTimer} design='section' />}
                </div>
                <hr className={classes.divider} />

                <div className={`${classes.questions_section} ${forceDoNotShowPages() ? classes.hidden : ''}`}>
                  <div className={classes.document_section} ref={scrollRef}>
                    <div className={classes.document}>
                      {cyberAssessmentData.cyberDocument?.content && (
                        <div className={classes.document_content}>
                          {cyberAssessmentData.cyberDocument.content}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={classes.questions}>
                    {
                      cyberAssessmentData.cyberTestInstance && cyberAssessmentData.cyberTestInstance.cyberQuestions.map((question, index) => {
                        return (
                          <div className={classes.question} key={question.id}>
                            <div className={classes.text}>
                              <b>{index + 1}. </b>
                              <div>{question.content}</div>
                            </div>
                            {question.questionType === 'TF' ? (
                              <div className={classes.options}>
                                <div>
                                  <label>
                                    <input
                                      type="radio"
                                      {...register(question.id.toString())}
                                      value="T"
                                      id={`question_${question.id}_true`}
                                      data-dd-privacy="allow"
                                    /> Yes
                                  </label>
                                </div>
                                <div>
                                  <label>
                                    <input
                                      type="radio"
                                      {...register(question.id.toString())}
                                      value="F"
                                      id={`question_${question.id}_false`}
                                      data-dd-privacy="allow"
                                    /> No
                                  </label>
                                </div>
                              </div>
                            ) : (
                              <div className={classes.options}>
                                {question.choices?.map((choice: string, idx: number) => (
                                  <div key={idx}>
                                    <label>
                                      <input
                                        type="radio"
                                        {...register(question.id.toString())}
                                        value={String.fromCharCode(65 + idx)}
                                        id={`question_${question.id}_choice_${idx}`}
                                        data-dd-privacy="allow"
                                      /> {choice}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )
                      })
                    }
                  </div>
                </div>
              </div>
              <div className={classes.buttons}>
                <div className={classes.cancel_button}>
                  <Button type='button' variant='outlined' onClickMethod={() => cancelCyberAssessment({ setStatus, setError })}>
                    Cancel Assessment
                  </Button>
                </div>
                <div className={classes.submit_button}>
                    <Button type='submit' hidden={forceDoNotShowPages()}>
                    {cyberAssessmentData.cyberTestInstance &&
                      cyberAssessmentData.cyberTestInstance.totalDocumentCount !== cyberAssessmentData.cyberTestInstance.documentNumber
                      ? 'Next Document'
                      : 'Submit'}
                    <Icon name='longArrowLeft' color='#FFF' transform />
                    </Button>
                </div>
              </div>
            </form>
          </div>
      }
    </div>
  )
}

export default StartCyberAssessment
